<template>
  <div class="flex flex-col gap-5">
    <BaseSection heading="Altgerätverifizierung" :is-main-heading="true">
      <BaseSection heading="ALT-SRT">
        <BaseInformation
          v-if="!noCurrentDeviceAvailable"
          :information-text="deviceVerificationText"
          :status="deviceVerificationStatus"
        />
        <div v-if="!noCurrentDeviceAvailable" class="md:flex md:gap-4">
          <BaseFormInput
            v-model="device.serialNumber"
            class="md:basis-1/2"
            :disabled="true"
            label="Seriennummer"
            :optional="false"
            test-id="deviceSerialnumber"
          >
            <template #trailing>
              <span
                class="mdi mdi-qrcode-scan text-3xl"
                @click="openDeviceCodeScanner"
              />
            </template>
          </BaseFormInput>
          <BaseFormInput
            v-model="device.customizedNumber"
            class="md:basis-1/2"
            :disabled="true"
            label="Artikelnummer"
            test-id="deviceProductkey"
          />
        </div>
        <div class="md:flex md:gap-4">
          <DateMount
            class="md:basis-1/2"
            :device-event="deviceEvent"
            label="Einbaudatum"
            :disabled="true"
            test-id="mountDate"
          />
          <DateMount
            class="md:basis-1/2"
            :device-event="recordingDate"
            label="Erfassungsdatum"
            :disabled="true"
            test-id="recordingDate"
          />
        </div>
        <TKDVerificationStatus
          v-model:tkd-verification-status="
            TkdService.deviceService.deviceVerificationStatus
          "
          verification-type="device"
          @change="onDeviceVerificationChange"
        />
      </BaseSection>
      <BaseSection
        v-if="!noCurrentDeviceAvailable && !formStore.isReplaceFormAction()"
        heading="Batterieeinheit"
      >
        <BaseInformation
          v-if="!noCurrentBatteryPack"
          :information-text="batteryPackVerificationText"
          :status="batteryPackVerificationStatus"
        />
        <div v-if="!noCurrentBatteryPack" class="md:flex md:gap-4">
          <BaseFormInput
            v-model="batteryPack.serialNumber"
            label="Seriennummer"
            class="md:basis-1/2"
            :disabled="true"
            type="text"
            test-id="serialNumberBatteryPack"
          >
            <template #trailing>
              <span
                class="mdi mdi-qrcode-scan text-3xl"
                @click="openDeviceCodeScanner"
              />
            </template>
          </BaseFormInput>
          <BaseFormInput
            v-model="batteryPack.customizedNumber"
            class="md:basis-1/2"
            :disabled="true"
            label="Artikelnummer"
            type="number"
            test-id="customizedNumberBatteryPack"
          />
        </div>

        <div class="mt-2">
          <TKDVerificationStatus
            v-model:tkd-verification-status="
              TkdService.deviceService.batteryVerificationStatus
            "
            verification-type="battery"
          />
        </div>
      </BaseSection>
    </BaseSection>
    <BaseSection
      v-if="!formStore.isReplaceFormAction()"
      heading="Foto"
      :is-main-heading="false"
      info="Installationspunkt zu Beginn der TKD-Maßnahme"
    >
      <ImageCaptureRef
        v-model:images="images"
        :object-id="installationPoint.id"
        :object-type="ImageObjectType.TKD_REPORT"
        class="mt-5"
      />
      <Checkbox
        v-model:checked="isImageNotAllowedChecked"
        test-id="resident-denied-photo"
        label="Nutzer verweigert Foto"
        class="mt-5"
      />
    </BaseSection>
  </div>
  <div v-if="isCodeScanActive">
    <DeviceCodeScanner
      :device-type="device.deviceType"
      @scan-result="scanResult($event)"
    />
  </div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DeviceClasses } from '@/models/devices/Device';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { DeviceEventParams } from '@/models/installationPoint/DeviceEvent';
import BaseSection from '@/components/UI/Card/BaseSection.vue';
import BaseFormInput from '@/components/UI/Input/BaseFormInput.vue';
import { AccessoryType } from '@/enums/device';
import DateMount from '@/components/Forms/DeviceFormComponents/DateMount.vue';
import DeviceCodeScanner from '@/components/UI/Scanner/DeviceCodeScanner.vue';
import BaseInformation from '@/components/UI/Hint/BaseInformation.vue';
import i18n from '@/utils/i18n/tkdService.json';
import Checkbox from '@/components/UI/Checkbox/Checkbox.vue';
import { Image, ImageObjectType } from '@/models/Image';
import { TKDService } from '@/models/installationPoint/TKDService';
import type { TKDVerificationStatus as TKDVerificationStatusModel } from '@/models/installationPoint/DeviceService';
import ImageCaptureRef from '@/components/Forms/DeviceFormComponents/ImageCaptureRef.vue';
import TKDVerificationStatus from '@/components/Forms/TkdServiceComponents/TKDVerificationStatus.vue';
import { useFormStore } from '@/store/form/formStore';
import useHardwareScanListener from '@/components/UI/Scanner/useHardwareScanListener';

const emit = defineEmits(['update:modelValue']);
const formStore = useFormStore();

const device = defineModel<DeviceClasses>('device');
const deviceEvent = defineModel<DeviceEventParams>('deviceEvent');
const installationPoint = defineModel<InstallationPoint>('installationPoint');
const TkdService = defineModel<TKDService>('tkdService');

const deviceService = TkdService.value?.deviceService;

const noCurrentDeviceAvailable = computed(() => {
  return deviceService?.deviceVerificationStatus.isVerificationNotPossible;
});

const noCurrentBatteryPack = computed(() => {
  return deviceService?.batteryVerificationStatus.isVerificationNotPossible;
});

const onDeviceVerificationChange = (isVerificationNotPossible: boolean) => {
  if (!deviceService) return;
  deviceService.batteryVerificationStatus.isVerificationNotPossible =
    isVerificationNotPossible;
  deviceService.batteryVerificationStatus.reason = undefined;
  deviceService.deviceVerificationStatus.reason = undefined;
};

const isCodeScanActive = ref(false);

const isDeviceVerified = ref(undefined);
const isBatteryPackVerified = ref(undefined);

const images = defineModel<Image[]>('images');

const batteryPack = computed(() => {
  if (!installationPoint.value?.accessories) {
    return undefined;
  }

  return installationPoint.value.accessories.find(
    (accessory) =>
      accessory.type === AccessoryType.SRT_BATTERY_PACK &&
      !accessory.unmountedAt
  );
});

const isImageNotAllowedChecked = ref<boolean>(false);

const deviceVerificationText = computed(() => {
  if (isDeviceVerified.value === undefined) {
    return i18n.deviceVerification.warning;
  }

  return isDeviceVerified.value
    ? i18n.deviceVerification.success
    : i18n.deviceVerification.error;
});

const deviceVerificationStatus = computed(() => {
  if (isDeviceVerified.value === undefined) {
    return 'warning';
  }
  return isDeviceVerified.value ? 'success' : 'error';
});

const batteryPackVerificationText = computed(() => {
  if (!isDeviceVerified.value) return i18n.deviceVerification.warning;
  if (noCurrentDeviceAvailable.value || !isDeviceVerified.value)
    return i18n.deviceVerification.warning;
  return isBatteryPackVerified.value
    ? i18n.deviceVerification.success
    : i18n.deviceVerification.error;
});

const batteryPackVerificationStatus = computed(() => {
  if (isBatteryPackVerified.value === undefined) {
    return 'warning';
  }
  return isBatteryPackVerified.value ? 'success' : 'error';
});

const recordingDate = computed(() => {
  return {
    timestamp: new Date().toISOString(),
  };
});

const openDeviceCodeScanner = () => {
  return (isCodeScanActive.value = true);
};

const scanResult = (scanDataObject: Record<string, unknown> | undefined) => {
  isCodeScanActive.value = false;
  if (!scanDataObject || Object.keys(scanDataObject).length === 0) return;

  if (scanDataObject.serialNumber) {
    isDeviceVerified.value =
      scanDataObject.serialNumber === device.value?.serialNumber;
  }
  if (scanDataObject.serialNumberBatteryPack) {
    const unknownProperties = scanDataObject.unknownProperties || undefined;
    isBatteryPackVerified.value = unknownProperties
      ? unknownProperties.includes(batteryPack.value?.serialNumber)
      : false;
  }
};

const hasValidReason = (status?: TKDVerificationStatusModel) => {
  if (!status?.reason) return false;
  if (status.reason === 'OTHER') {
    return !!status.note;
  }
  return true;
};

const hasRequiredImages = () => {
  if (formStore.isReplaceFormAction()) {
    return true;
  }
  if (isImageNotAllowedChecked.value) return true;
  return !!(images.value && images.value.length > 0);
};

const isLocalFormValid = computed(() => {
  if (noCurrentDeviceAvailable.value) {
    return (
      hasValidReason(deviceService?.deviceVerificationStatus) &&
      hasRequiredImages()
    );
  }

  if (!isDeviceVerified.value) return false;

  if (formStore.isReplaceFormAction()) {
    return true;
  }

  if (isBatteryPackVerified.value) {
    return true;
  }

  if (noCurrentBatteryPack.value) {
    return hasValidReason(deviceService?.batteryVerificationStatus);
  }

  return false;
});

watch(isLocalFormValid, (val) => {
  emit('update:modelValue', val);
});

useHardwareScanListener(scanResult, () => device.value?.deviceType);

defineExpose({
  isFormValid: isLocalFormValid,
});
</script>
